import React from 'react';
import styles from './WorldCupBrackets.module.css';

// Layout constants derived from Figma connector SVG dimensions (px)
// Figma small connector SVG height = 140 → center-to-center between paired cards = 140
// Match card content: 12px pad + 15px status + 15px gap + 28px row + 8px gap + 28px row + 12px pad = 118px
const MATCH_H   = 118;  // height of a single match card (exact Figma card content)
const MATCH_GAP = 22;   // gap between cards within the same round
const SLOT      = MATCH_H + MATCH_GAP;  // 140 px — matches Figma connector SVG height
const P0        = SLOT / 2;             // 70 px — base padding-top for R32

const BRACKET_SLOT_MAP = {
    // Round of 32 (1/16 Finals) - Slots 0 to 15
    6311567: 0,  // South Africa - Canada
    6311569: 1,  // Netherlands - Morocco
    6311565: 2,  // Germany - Paraguay
    6311566: 3,  // France - Sweden
    6311576: 4,  // Brazil - Japan
    6311577: 5,  // Ivory Coast - Norway
    6311578: 6,  // Mexico - Ecuador
    6311579: 7,  // England - DR Congo
    6311572: 8,  // Portugal - Croatia
    6311573: 9,  // Spain - Austria
    6311575: 10, // Belgium - Senegal
    6311574: 11, // USA - Bosnia-Herzegovina
    6311582: 12, // Switzerland - Algeria
    6311583: 13, // Colombia - Ghana
    6311581: 14, // Australia - Egypt
    6311580: 15, // Argentina - Cape Verde

    // Round of 16 (1/8 Finals) - Slots 0 to 7
    6311585: 0,  // Canada - Morocco
    6311584: 1,  // Paraguay - France
    6311588: 2,  // Brazil - Norway
    6311589: 3,  // Mexico - England
    6311586: 4,  // Portugal - Spain
    6583050: 5,  // USA - Belgium
    6311587: 5,  // Winner Match 81 - Senegal (placeholder)
    6311591: 6,  // Switzerland - Colombia
    6311590: 7,  // Argentina - Egypt

    // Quarter Finals - Slots 0 to 3
    6311592: 0,  // France - Morocco
    6311594: 1,  // Norway - England
    6311593: 2,  // Spain - Belgium
    6311595: 3,  // Argentina - Switzerland

    // Semi Finals - Slots 0 to 1
    6311597: 0,  // Winner Match 97 - Winner Match 98
    6311598: 1,  // Winner Match 99 - Winner Match 100

    // Final - Slot 0
    6311600: 0   // Winner Match 101 - Winner Match 102
};

const countryFlags = {
  "Brazil": "br", "Argentina": "ar", "France": "fr", "Germany": "de",
  "Spain": "es", "Portugal": "pt", "Italy": "it", "Netherlands": "nl",
  "England": "gb-eng", "Belgium": "be", "Uruguay": "uy", "Croatia": "hr",
  "Senegal": "sn", "Japan": "jp", "Morocco": "ma", "USA": "us", "United States": "us",
  "Qatar": "qa", "UAE": "ae", "Saudi Arabia": "sa", "Kuwait": "kw",
  "Canada": "ca", "Mexico": "mx", "Australia": "au", "South Korea": "kr",
  "Poland": "pl", "Switzerland": "ch", "Denmark": "dk", "Tunisia": "tn",
  "Cameroon": "cm", "Ecuador": "ec", "Costa Rica": "cr", "Wales": "gb-wls",
  "Iran": "ir", "Ghana": "gh"
};

const getFlagUrl = (name) => {
  if (!name) return null;
  const code = countryFlags[name] || countryFlags[name.trim()];
  if (code) {
    return `https://flagcdn.com/w20/${code}.png`;
  }
  return null;
};

const cleanTeamName = (name) => {
  return name;
};

const getScoreDetails = (participant) => {
  if (!participant || !participant.results) return null;

  const results = participant.results;
  const resultObj = results.find(r => String(r.id) === '2' || r.short_name === 'Result');
  const penaltyObj = results.find(r => String(r.id) === '7' || r.short_name === 'Penalty');

  const resultVal = resultObj?.value;
  const penaltyVal = penaltyObj?.value;

  if (resultVal === undefined || resultVal === null || resultVal === '') {
    return null;
  }

  return {
    goals: resultVal,
    penalty: (penaltyVal !== undefined && penaltyVal !== null && penaltyVal !== '') ? penaltyVal : null
  };
};

const getWinnerStatus = (participant, event) => {
  if (!participant || !event) return null;
  
  const statusLower = (event.status_name || '').trim().toLowerCase();
  const statusTypeLower = (event.status_type || '').trim().toLowerCase();

  // If Not started, do not show W or L
  if (statusLower === 'not started' || statusTypeLower === 'scheduled') {
    return null;
  }

  // If Finished, determine W/L based on Progress (id: 412) primarily
  const isFinished = statusTypeLower === 'finished' || statusLower.includes('finished');
  if (isFinished && participant.results) {
    // 1. Check Progress (id: 412)
    const progressObj = participant.results.find(r => String(r.id) === '412' || r.short_name === 'Progress');
    if (progressObj && (progressObj.value === '1' || progressObj.value === 'yes')) return 'W';
    if (progressObj && (progressObj.value === '0' || progressObj.value === 'no')) return 'L';
    
    // 2. Check Winner (id: 411)
    const winnerObj = participant.results.find(r => String(r.id) === '411' || r.short_name === 'Winner');
    if (winnerObj && (winnerObj.value === '1' || winnerObj.value === 'yes')) return 'W';
    if (winnerObj && (winnerObj.value === '0' || winnerObj.value === 'no')) return 'L';
  }

  // Fallback to winner_id if match is finished but results array was empty
  if (isFinished && event.winner_id && participant.id) {
    return String(event.winner_id) === String(participant.id) ? 'W' : 'L';
  }

  return null;
};

export default function WorldCupBrackets({ events }) {
    const mapEventToMatchup = (event) => {
        if (!event) return null;
        const home = event.participants?.[0];
        const away = event.participants?.[1];

        const homeName = cleanTeamName(home?.name);
        const awayName = cleanTeamName(away?.name);

        const homeFlag = getFlagUrl(home?.name);
        const awayFlag = getFlagUrl(away?.name);

        const homeScoreDetails = getScoreDetails(home);
        const awayScoreDetails = getScoreDetails(away);

        const homeWinStatus = getWinnerStatus(home, event);
        const awayWinStatus = getWinnerStatus(away, event);

        let status = 'Scheduled';
        if (homeScoreDetails !== null || awayScoreDetails !== null) {
            status = event.winner_id ? 'Full Time' : 'Live';
        }

        return {
            id: event.id,
            status,
            t1: {
                name: homeName,
                flag: homeFlag,
                winStatus: homeWinStatus,
                scoreDetails: homeScoreDetails
            },
            t2: {
                name: awayName,
                flag: awayFlag,
                winStatus: awayWinStatus,
                scoreDetails: awayScoreDetails
            }
        };
    };

    const padArray = (arr, length) => {
        const padded = [...arr];
        while (padded.length < length) {
            padded.push(null);
        }
        return padded.slice(0, length);
    };

    const alignBracketMatches = (currentRoundMatches, previousRoundMatches) => {
        if (!previousRoundMatches || previousRoundMatches.length === 0) {
            return currentRoundMatches;
        }

        const isTeamFromMatch = (teamName, parentMatch) => {
            if (!teamName || !parentMatch) return false;
            const n1 = (parentMatch.t1?.name || '').toLowerCase();
            const n2 = (parentMatch.t2?.name || '').toLowerCase();
            const target = teamName.toLowerCase();
            return n1.includes(target) || target.includes(n1) || n2.includes(target) || target.includes(n2);
        };

        return currentRoundMatches.map((match, slotIdx) => {
            if (!match || !match.t1 || !match.t2) return match;

            const parentTop = previousRoundMatches[2 * slotIdx];
            const parentBottom = previousRoundMatches[2 * slotIdx + 1];

            if (isTeamFromMatch(match.t1.name, parentBottom) || isTeamFromMatch(match.t2.name, parentTop)) {
                return {
                    ...match,
                    t1: match.t2,
                    t2: match.t1
                };
            }
            return match;
        });
    };

    // ── Empty state ─────────────────────────────────────────────────────────
    if (!events || events.length === 0) {
        return (
            <section className={styles.bracketSectionWrapper}>
                <h2 className={styles.bracketHeading}>Knockout Tree</h2>
                <p className={styles.bracketStatusMsg}>Knockout stage bracket will be available soon.</p>
            </section>
        );
    }

    // ── Map rounds from events with stale placeholder filtering ────────────
    const ROUND_ORDER = ['1/16 Finals', '1/8 Finals', 'Quarter Finals', 'Semi Finals', 'Final'];

    const getEliminatedBefore = (roundName) => {
        const targetIdx = ROUND_ORDER.indexOf(roundName);
        const eliminated = new Set();
        events.forEach(event => {
            const roundIdx = ROUND_ORDER.indexOf(event.round_name);
            if (roundIdx !== -1 && roundIdx < targetIdx) {
                const statusTypeLower = (event.status_type || '').trim().toLowerCase();
                const statusLower = (event.status_name || '').trim().toLowerCase();
                const isFinished = statusTypeLower === 'finished' || statusLower.includes('finished');
                if (isFinished && event.participants) {
                    const home = event.participants[0];
                    const away = event.participants[1];
                    const homeWin = getWinnerStatus(home, event);
                    const awayWin = getWinnerStatus(away, event);
                    if (homeWin === 'L' && home?.id) eliminated.add(home.id);
                    if (awayWin === 'L' && away?.id) eliminated.add(away.id);
                }
            }
        });
        return eliminated;
    };

    const rawR32   = events.filter(e => e.round_name === '1/16 Finals');
    const rawR16   = events.filter(e => e.round_name === '1/8 Finals');
    const rawQF    = events.filter(e => e.round_name === 'Quarter Finals');
    const rawSF    = events.filter(e => e.round_name === 'Semi Finals');
    const rawFinal = events.filter(e => e.round_name === 'Final');

    const elim16 = getEliminatedBefore('1/8 Finals');
    const elimQF = getEliminatedBefore('Quarter Finals');
    const elimSF = getEliminatedBefore('Semi Finals');
    const elimFinal = getEliminatedBefore('Final');

    const filteredR16 = rawR16.filter(e => !e.participants?.some(p => elim16.has(p.id)));
    const filteredQF = rawQF.filter(e => !e.participants?.some(p => elimQF.has(p.id)));
    const filteredSF = rawSF.filter(e => !e.participants?.some(p => elimSF.has(p.id)));
    const filteredFinal = rawFinal.filter(e => !e.participants?.some(p => elimFinal.has(p.id)));

    const getStructuralMatches = (rawMatches, size) => {
        const slots = Array(size).fill(null);
        rawMatches.forEach(e => {
            const slotIdx = BRACKET_SLOT_MAP[e.id];
            if (slotIdx !== undefined && slotIdx >= 0 && slotIdx < size) {
                slots[slotIdx] = mapEventToMatchup(e);
            }
        });
        return slots;
    };

    const r32Matches   = getStructuralMatches(rawR32, 16);
    const r16Matches   = alignBracketMatches(getStructuralMatches(filteredR16, 8), r32Matches);
    const qfMatches    = alignBracketMatches(getStructuralMatches(filteredQF, 4), r16Matches);
    const sfMatches    = alignBracketMatches(getStructuralMatches(filteredSF, 2), qfMatches);
    const finalMatches = alignBracketMatches(getStructuralMatches(filteredFinal, 1), sfMatches);

    // Padding-top for each round (shifted up by 30px to make R32 top padding 40px while preserving connector alignment)
    const pt = {
        r32:   40,          // 70 - 30
        r16:   110,         // 140 - 30
        qf:    250,         // 280 - 30
        sf:    530,         // 560 - 30
        final: 1090,        // 1120 - 30
    };

    // Gap between match cards per round
    const gap = {
        r32:   MATCH_GAP,           // 22
        r16:   SLOT * 2  - MATCH_H, // 162
        qf:    SLOT * 4  - MATCH_H, // 442
        sf:    SLOT * 8  - MATCH_H, // 1002
        final: 0,
    };

    return (
        <section className={styles.bracketSectionWrapper}>
            <h2 className={styles.bracketHeading}>Knockout Tree</h2>

            {/* ── Full horizontally-scrollable bracket ── */}
            <div className={styles.bracketScroll}>
                <div className={styles.bracketRow}>

                    <RoundColumn
                        label="Round of 32"
                        matches={r32Matches}
                        paddingTop={pt.r32}
                        gap={gap.r32}
                        flex
                    />
                    <ConnectorCol
                        count={r16Matches.length}
                        slotH={SLOT}
                        leftPaddingTop={pt.r32}
                    />

                    <RoundColumn
                        label="Round of 16"
                        matches={r16Matches}
                        paddingTop={pt.r16}
                        gap={gap.r16}
                    />
                    <ConnectorCol
                        count={qfMatches.length}
                        slotH={SLOT * 2}
                        leftPaddingTop={pt.r16}
                    />

                    <RoundColumn
                        label="Quarter Finals"
                        matches={qfMatches}
                        paddingTop={pt.qf}
                        gap={gap.qf}
                    />
                    <ConnectorCol
                        count={sfMatches.length}
                        slotH={SLOT * 4}
                        leftPaddingTop={pt.qf}
                    />

                    <RoundColumn
                        label="Semi Finals"
                        matches={sfMatches}
                        paddingTop={pt.sf}
                        gap={gap.sf}
                    />
                    <ConnectorCol
                        count={1}
                        slotH={SLOT * 8}
                        leftPaddingTop={pt.sf}
                    />

                    <RoundColumn
                        label="Final"
                        matches={finalMatches}
                        paddingTop={pt.final}
                        gap={gap.final}
                    />

                </div>
            </div>
        </section>
    );
}

// Helper components
const MatchCard = ({ match }) => {
    if (!match) return <div className={styles.bracketMatchPlaceholder} />;
    return (
        <div className={styles.matchCard}>
            <span className={styles.matchStatus}>{match.status}</span>
            <div className={styles.bracketTeamRowsContainer}>
                <div className={styles.bracketTeamRow}>
                    <div className={styles.bracketTeamInfo}>
                        {match.t1.flag ? (
                            <img src={match.t1.flag} alt="" className={styles.bracketTeamFlagImg} />
                        ) : (
                            <span className={styles.bracketTeamFlag} />
                        )}
                        <span className={styles.bracketTeamName}>{match.t1.name}</span>
                        {match.t1.scoreDetails && (
                            <>
                                <span className={styles.bracketSeparatorDot}>•</span>
                                <span className={styles.bracketRoundTag}>{match.t1.scoreDetails.goals}</span>
                                {match.t1.scoreDetails.penalty && (
                                    <>
                                        <span className={styles.bracketSeparatorDot}>•</span>
                                        <span className={styles.bracketRoundTag}>({match.t1.scoreDetails.penalty})</span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {match.t1.winStatus && (
                        <span className={`${styles.bracketResultBadge} ${match.t1.winStatus === 'L' ? styles.bracketResultBadgeLoss : ''}`}>
                            {match.t1.winStatus}
                        </span>
                    )}
                </div>
                <div className={styles.bracketTeamRow}>
                    <div className={styles.bracketTeamInfo}>
                        {match.t2.flag ? (
                            <img src={match.t2.flag} alt="" className={styles.bracketTeamFlagImg} />
                        ) : (
                            <span className={styles.bracketTeamFlag} />
                        )}
                        <span className={styles.bracketTeamName}>{match.t2.name}</span>
                        {match.t2.scoreDetails && (
                            <>
                                <span className={styles.bracketSeparatorDot}>•</span>
                                <span className={styles.bracketRoundTag}>{match.t2.scoreDetails.goals}</span>
                                {match.t2.scoreDetails.penalty && (
                                    <>
                                        <span className={styles.bracketSeparatorDot}>•</span>
                                        <span className={styles.bracketRoundTag}>({match.t2.scoreDetails.penalty})</span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {match.t2.winStatus && (
                        <span className={`${styles.bracketResultBadge} ${match.t2.winStatus === 'L' ? styles.bracketResultBadgeLoss : ''}`}>
                            {match.t2.winStatus}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const BracketConnectors = ({ count, slotH }) => {
    const pairH  = slotH * 2;
    const totalH = pairH * count;
    const topY   = MATCH_H / 2;          // relative to pair top — center of slot 0
    const botY   = slotH + MATCH_H / 2;  // relative to pair top — center of slot 1
    const midY   = (topY + botY) / 2;
    const r      = 4;

    return (
        <svg
            className={styles.connectorSvg}
            width="49"
            height={totalH}
            viewBox={`0 0 49 ${totalH}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            {Array.from({ length: count }, (_, i) => {
                const dy = i * pairH;
                const t  = dy + topY;
                const b  = dy + botY;
                const m  = dy + midY;
                return (
                    <g key={i}>
                        {/* Top arm: left → right-and-down → midpoint-right */}
                        <path
                            d={`M0,${t} H14 Q18,${t} 18,${t + r} V${m - r} Q18,${m} 22,${m} H49`}
                            stroke="#D4AF37"
                            strokeWidth="1"
                        />
                        {/* Bottom arm: left → right-and-up → midpoint-right */}
                        <path
                            d={`M0,${b} H14 Q18,${b} 18,${b - r} V${m + r} Q18,${m} 22,${m} H49`}
                            stroke="#D4AF37"
                            strokeWidth="1"
                        />
                    </g>
                );
            })}
        </svg>
    );
};

const RoundColumn = ({ label, matches, paddingTop, gap, flex }) => (
    <div className={flex ? styles.roundColumnFlex : styles.roundColumn}>
        <div className={styles.roundHeader}>{label}</div>
        <div
            className={styles.matchList}
            style={{ paddingTop, gap }}
        >
            {matches.map((m, i) => (
                <div
                    key={m?.id ?? i}
                    className={styles.matchSlot}
                    style={{ height: MATCH_H }}
                >
                    <MatchCard match={m} />
                </div>
            ))}
        </div>
    </div>
);

const HEADER_H = 51; // round header height: 16px pad-top + ~19px text + 16px pad-bottom

const ConnectorCol = ({ count, slotH, leftPaddingTop }) => (
    <div
        className={styles.connectorCol}
        style={{ paddingTop: HEADER_H + leftPaddingTop }}
    >
        <BracketConnectors count={count} slotH={slotH} />
    </div>
);
